import type { ContactOrderBatchResponse } from "../../../../../api/kehrblatt/contact-orders"
import type { ContactOrderAppointmentRow } from "../contact-orders-contract"
import {
  hasExplicitCommunicationSelection,
  hasActiveCommunicationForPlanningRow,
  type ContactOrderSelectionState,
} from "../selection-state"

export type ContactOrderCreateProjection = {
  created: ContactOrder[]
  failedIds: Set<string>
  events: ScheduleEvent[]
  contactOrders: ContactOrder[]
}

const isConfirmationInformationRow = (row: ContactOrderAppointmentRow) =>
  String(row.customerInformationMode ?? "").startsWith("confirmation")

export const contactOrderTargetEvents = ({
  pendingContactOrders,
  appointmentRows,
  selectionState,
  allowedContactChannels,
  includeAutomaticRows = false,
}: {
  pendingContactOrders: ScheduleEvent[]
  appointmentRows: ContactOrderAppointmentRow[]
  selectionState: ContactOrderSelectionState
  allowedContactChannels: readonly string[]
  includeAutomaticRows?: boolean
}) => {
  const rowByEventId = new Map(appointmentRows.map((row) => [row.id, row]))

  return pendingContactOrders.filter((event) => {
    if (!event.work_order_id) return false

    const row = rowByEventId.get(event.id)

    return Boolean(
      row &&
      (isConfirmationInformationRow(row) ||
        (includeAutomaticRows && !hasExplicitCommunicationSelection(row, selectionState)) ||
        hasActiveCommunicationForPlanningRow(row, selectionState, allowedContactChannels)),
    )
  })
}

export const projectContactOrderCreateResult = ({
  result,
  targetEvents,
  events,
  contactOrders,
}: {
  result: ContactOrderBatchResponse
  targetEvents: ScheduleEvent[]
  events: ScheduleEvent[]
  contactOrders: ContactOrder[]
}): ContactOrderCreateProjection => {
  const targetIds = new Set(targetEvents.map((entry) => entry.id))
  const eventIdByIndex = targetEvents.map((event) => event.id)
  const created = Array.isArray(result?.created) ? result.created : []
  const failedIds = new Set(
    (result?.failed || [])
      .map((entry) => entry.schedule_event_id || eventIdByIndex[entry.index])
      .filter(Boolean),
  )
  const createdByEventId = new Map(
    created
      .map((order) => [order.schedule_event_id ?? null, order] as const)
      .filter(([eventId]) => Boolean(eventId)),
  )
  const nextEvents = events.map((event) => {
    if (!targetIds.has(event.id)) return event

    const linkedOrder = createdByEventId.get(event.id)

    if (!linkedOrder) return event

    return {
      ...event,
      contact_order_id: linkedOrder.id,
    }
  })
  const contactOrdersById = new Map(contactOrders.map((order) => [order.id, order]))
  created.forEach((order) => {
    if (order.id) contactOrdersById.set(order.id, order)
  })

  return {
    created,
    failedIds,
    events: nextEvents,
    contactOrders: Array.from(contactOrdersById.values()),
  }
}
